import React from 'react';
import capitalize from 'lodash/capitalize';
import memoize from 'lodash/memoize';
import isNil from 'lodash/isNil';
import defaultTo from 'lodash/defaultTo';
import get from 'lodash/get';
import { Text } from '@atlaskit/primitives';
import { CloudSiteProduct, PricingPlan } from '@css-xp/graphtypes/src/generated-types-new';
import { BillingProfileNameFromId } from './billing-profile-name-from-id';
import { NoData } from './no-data';

export enum ORG_DETAILS_COLUMNS {
  SUBSCRIPTION = 'Subscription',
  STATUS = 'Status',
  PLAN = 'Plan',
  QUANTITY = 'Quantity',
  BILLING_PROFILE = 'Billing profile',
  RENEW_DATE = 'Renew date',
}

export const getOrgDetailsHeaders = memoize(() => {
  return [
    {
      name: ORG_DETAILS_COLUMNS.SUBSCRIPTION,
      content: ORG_DETAILS_COLUMNS.SUBSCRIPTION,
      customClassNames: 'col-20',
    },
    {
      name: ORG_DETAILS_COLUMNS.STATUS,
      content: ORG_DETAILS_COLUMNS.STATUS,
    },
    {
      name: ORG_DETAILS_COLUMNS.PLAN,
      content: ORG_DETAILS_COLUMNS.PLAN,
    },
    {
      name: ORG_DETAILS_COLUMNS.QUANTITY,
      content: ORG_DETAILS_COLUMNS.QUANTITY,
    },
    {
      name: ORG_DETAILS_COLUMNS.BILLING_PROFILE,
      content: ORG_DETAILS_COLUMNS.BILLING_PROFILE,
    },
    {
      name: ORG_DETAILS_COLUMNS.RENEW_DATE,
      content: ORG_DETAILS_COLUMNS.RENEW_DATE,
    },
  ];
});

export const getSortedPricingPlanTiers = (tiers: any) => {
  if (!tiers?.length) return [];
  return [...tiers].sort((a, b) => defaultTo(get(a, 'floor'), 0) - defaultTo(get(b, 'floor'), 0));
};

export const getUserTierBasedOnBilledUser = (billedUsers?: string | null, pricingPlanDetails?: PricingPlan) => {
  const userTiers = getSortedPricingPlanTiers(pricingPlanDetails?.tiers ?? []);
  const selectedUserTier =
    userTiers.find((userTierObj) => {
      if (!isNil(userTierObj.ceiling) && !isNil(userTierObj.floor) && billedUsers) {
        return billedUsers <= userTierObj.ceiling && billedUsers > userTierObj.floor;
      }
    }) ?? userTiers[0];
  return selectedUserTier;
};

export const renderTitleSubtitle = (title?: string | null, subTitle?: string | null): JSX.Element => {
  return (
    <div>
      <Text>{title}</Text>
      <br />
      <Text color="color.text.subtle" size="small">
        {subTitle}
      </Text>
    </div>
  );
};

export const renderPlan = (productSubscription: CloudSiteProduct) => {
  const title = productSubscription?.offering?.name ?? productSubscription?.edition ?? '';
  const subtitle = capitalize(productSubscription?.billingFrequency || '');
  if (title || subtitle) {
    return renderTitleSubtitle(title, subtitle);
  }
  return <NoData />;
};

export const renderQuantity = (productSubscription: CloudSiteProduct) => {
  const { activeUsers, userTier: maxUserTier, pricingPlan } = productSubscription;
  const billedUsers = productSubscription.billedUsers;
  const userTier = getUserTierBasedOnBilledUser(billedUsers, pricingPlan as PricingPlan);
  if (userTier) {
    const numberOfUserTiers = userTier.ceiling - userTier.floor;
    const seatsLeft = activeUsers ? Number(numberOfUserTiers ?? maxUserTier) - Number(activeUsers) : maxUserTier;
    const seatsText = seatsLeft === 1 ? 'seat' : 'seats';
    return renderTitleSubtitle(`${userTier.floor}-${userTier.ceiling}`, `${seatsLeft} ${seatsText} available`);
  }
  return <NoData />;
};

export const renderBillingProfile = (
  productSubscription: CloudSiteProduct,
  txaId: string,
  billingProfileData = null
) => {
  return (
    <BillingProfileNameFromId
      id={productSubscription.invoiceGroupId}
      txaId={txaId}
      showBillingProfileNumber={true}
      invoiceGroupData={billingProfileData}
    />
  );
};
